import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Users, DollarSign, TrendingUp, Activity, 
  LogOut, UserCheck, UserX, Ban, CheckCircle,
  CreditCard, ArrowUpCircle, ArrowDownCircle, MessageSquare, Key, Trash2, CheckCircle2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { useToast } from '../hooks/use-toast';
import { adminAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import SessionTimeoutModal from '../components/SessionTimeoutModal';
import sessionManager from '../utils/sessionManager';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedUser, setSelectedUser] = useState(null);
  const [transactionData, setTransactionData] = useState({ amount: '', description: '' });
  const [showTransactionDialog, setShowTransactionDialog] = useState(false);
  const [adminPasswordData, setAdminPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [chatMessages, setChatMessages] = useState([]);
  const [selectedChatUser, setSelectedChatUser] = useState(null);
  const [chatReply, setChatReply] = useState('');
  const [showChatDialog, setShowChatDialog] = useState(false);
  
  // Session timeout
  const [showSessionWarning, setShowSessionWarning] = useState(false);

  useEffect(() => {
    fetchDashboardData();
    
    // Start session timeout manager
    sessionManager.start(
      () => setShowSessionWarning(true),  // Show warning at 14 minutes
      () => handleSessionTimeout()        // Force logout at 15 minutes
    );
    
    // Cleanup on unmount
    return () => {
      sessionManager.stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [statsData, usersData, transactionsData, messagesData] = await Promise.all([
        adminAPI.getDashboardStats(),
        adminAPI.getUsers(),
        adminAPI.getTransactions(),
        adminAPI.getMessages()
      ]);
      
      setStats(statsData);
      setUsers(usersData.users);
      setTransactions(transactionsData.transactions);
      setMessages(messagesData.messages);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch dashboard data",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (userId) => {
    try {
      await adminAPI.approveUser(userId);
      toast({ title: "Success", description: "User approved successfully" });
      fetchDashboardData();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to approve user",
        variant: "destructive"
      });
    }
  };

  const handleSuspend = async (userId) => {
    try {
      await adminAPI.suspendUser(userId);
      toast({ title: "Success", description: "User suspended successfully" });
      fetchDashboardData();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to suspend user",
        variant: "destructive"
      });
    }
  };

  const handleTerminate = async (userId) => {
    try {
      await adminAPI.terminateUser(userId);
      toast({ title: "Success", description: "User terminated successfully" });
      fetchDashboardData();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to terminate user",
        variant: "destructive"
      });
    }
  };

  const handleResetPassword = async (userId) => {
    if (!window.confirm("Are you sure you want to reset this user's password?")) {
      return;
    }
    
    try {
      const response = await adminAPI.resetPassword(userId);
      toast({ 
        title: "Password Reset Successfully", 
        description: `Temporary Password: ${response.temporaryPassword}. Account: ${response.accountNumber}. User will be notified.`,
        duration: 15000
      });
      fetchDashboardData();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to reset password",
        variant: "destructive"
      });
    }
  };

  const handleAdminPasswordChange = async (e) => {
    e.preventDefault();
    
    if (adminPasswordData.newPassword !== adminPasswordData.confirmPassword) {
      toast({
        title: "Error",
        description: "Passwords do not match",
        variant: "destructive"
      });
      return;
    }
    
    try {
      await adminAPI.changePassword(adminPasswordData.currentPassword, adminPasswordData.newPassword);
      toast({
        title: "Password Changed",
        description: "Your admin password has been changed successfully",
      });
      setAdminPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast({
        title: "Change Failed",
        description: error.response?.data?.detail || "Failed to change password",
        variant: "destructive"
      });
    }
  };

  const handleDeleteTransaction = async (transactionId) => {
    if (!window.confirm("Are you sure you want to delete this transaction? This action cannot be undone.")) {
      return;
    }
    
    try {
      await adminAPI.deleteTransaction(transactionId);
      toast({ title: "Success", description: "Transaction deleted successfully" });
      fetchDashboardData();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete transaction",
        variant: "destructive"
      });
    }
  };

  const handleActivateUser = async (userId) => {
    try {
      await adminAPI.activateUser(userId);
      toast({ title: "Success", description: "User account activated successfully" });
      fetchDashboardData();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to activate user",
        variant: "destructive"
      });
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to permanently delete ${userName}'s account? This action cannot be undone and will delete all associated data.`)) {
      return;
    }
    
    try {
      await adminAPI.deleteUser(userId);
      toast({ 
        title: "User Deleted", 
        description: "User account and all associated data have been permanently deleted" 
      });
      fetchDashboardData();
    } catch (error) {
      toast({
        title: "Delete Failed",
        description: error.response?.data?.detail || "Failed to delete user",
        variant: "destructive"
      });
    }
  };

  const handleSuspendWireTransfer = async (userId, userName) => {
    if (!window.confirm(`Suspend wire transfer privileges for ${userName}?`)) {
      return;
    }
    
    try {
      await adminAPI.suspendWireTransfer(userId);
      toast({ 
        title: "Wire Transfer Suspended", 
        description: "User can no longer make wire transfers" 
      });
      fetchDashboardData();
    } catch (error) {
      toast({
        title: "Suspension Failed",
        description: error.response?.data?.detail || "Failed to suspend wire transfers",
        variant: "destructive"
      });
    }
  };

  const handleUnsuspendWireTransfer = async (userId, userName) => {
    if (!window.confirm(`Restore wire transfer privileges for ${userName}?`)) {
      return;
    }
    
    try {
      await adminAPI.unsuspendWireTransfer(userId);
      toast({ 
        title: "Wire Transfer Restored", 
        description: "User can now make wire transfers" 
      });
      fetchDashboardData();
    } catch (error) {
      toast({
        title: "Restoration Failed",
        description: error.response?.data?.detail || "Failed to restore wire transfers",
        variant: "destructive"
      });
    }
  };

  const fetchChatMessages = async () => {
    try {
      const response = await adminAPI.getChatMessages();
      setChatMessages(response.messages || []);
    } catch (error) {
      console.error("Failed to fetch chat messages:", error);
    }
  };

  const handleChatReply = async (e) => {
    e.preventDefault();
    if (!chatReply.trim() || !selectedChatUser) return;

    try {
      await adminAPI.replyChatMessage(selectedChatUser, chatReply);
      toast({ title: "Reply Sent", description: "Your message has been sent to the customer" });
      setChatReply('');
      fetchChatMessages();
      setShowChatDialog(false);
    } catch (error) {
      toast({
        title: "Send Failed",
        description: "Failed to send reply",
        variant: "destructive"
      });
    }
  };

  const handleCredit = async () => {
    if (!selectedUser) return;
    try {
      await adminAPI.creditUser(selectedUser.id, parseFloat(transactionData.amount), transactionData.description);
      toast({ title: "Success", description: "Account credited successfully" });
      setTransactionData({ amount: '', description: '' });
      setSelectedUser(null);
      setShowTransactionDialog(false);
      fetchDashboardData();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to credit account",
        variant: "destructive"
      });
    }
  };

  const handleDebit = async () => {
    if (!selectedUser) return;
    try {
      await adminAPI.debitUser(selectedUser.id, parseFloat(transactionData.amount), transactionData.description);
      toast({ title: "Success", description: "Account debited successfully" });
      setTransactionData({ amount: '', description: '' });
      setSelectedUser(null);
      setShowTransactionDialog(false);
      fetchDashboardData();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to debit account",
        variant: "destructive"
      });
    }
  };

  const handleLogout = () => {
    sessionManager.stop();
    logout();
    navigate('/admin/login');
  };
  
  const handleSessionTimeout = () => {
    toast({
      title: "Session Expired",
      description: "You have been logged out due to inactivity",
      variant: "destructive"
    });
    handleLogout();
  };
  
  const handleExtendSession = () => {
    setShowSessionWarning(false);
    sessionManager.extendSession();
    toast({
      title: "Session Extended",
      description: "Your session has been extended",
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 relative">
      {/* Background Image with Overlay */}
      <div 
        className="fixed inset-0 bg-cover bg-center bg-no-repeat opacity-10"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1736325263301-488931888151?q=85&w=1920)',
          zIndex: 0
        }}
      ></div>
      
      {/* Content Wrapper */}
      <div className="relative z-10">
        {/* Header */}
        <header className="bg-gray-800/95 backdrop-blur-sm shadow-lg border-b border-gray-700">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex justify-between items-center">
              <div>
                <h1 className="text-2xl font-bold text-white">Admin Dashboard</h1>
                <p className="text-sm text-gray-300">Welcome back, {user?.name}</p>
              </div>
              <Button onClick={handleLogout} variant="outline" className="flex items-center border-gray-600 text-gray-200 hover:bg-gray-700">
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </header>

        {/* Tabs */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
          <div className="flex space-x-4 border-b border-gray-700">
            <button
              onClick={() => setActiveTab('overview')}
              className={`px-4 py-2 font-medium transition-colors ${activeTab === 'overview' ? 'border-b-2 border-blue-500 text-blue-400' : 'text-gray-400 hover:text-gray-200'}`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('users')}
              className={`px-4 py-2 font-medium transition-colors ${activeTab === 'users' ? 'border-b-2 border-blue-500 text-blue-400' : 'text-gray-400 hover:text-gray-200'}`}
            >
              User Management
            </button>
            <button
              onClick={() => setActiveTab('transactions')}
              className={`px-4 py-2 font-medium transition-colors ${activeTab === 'transactions' ? 'border-b-2 border-blue-500 text-blue-400' : 'text-gray-400 hover:text-gray-200'}`}
            >
              Transactions
            </button>
            <button
              onClick={() => setActiveTab('messages')}
              className={`px-4 py-2 font-medium transition-colors ${activeTab === 'messages' ? 'border-b-2 border-blue-500 text-blue-400' : 'text-gray-400 hover:text-gray-200'}`}
            >
              Messages
            </button>
            <button
              onClick={() => setActiveTab('settings')}
              className={`px-4 py-2 font-medium transition-colors ${activeTab === 'settings' ? 'border-b-2 border-blue-500 text-blue-400' : 'text-gray-400 hover:text-gray-200'}`}
            >
              Settings
            </button>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Overview Tab */}
        {activeTab === 'overview' && stats && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">Total Users</CardTitle>
                  <Users className="w-4 h-4 text-blue-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{stats.totalUsers}</div>
                  <p className="text-xs text-gray-600 mt-1">Active: {stats.activeUsers}</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">Pending Approvals</CardTitle>
                  <UserCheck className="w-4 h-4 text-orange-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{stats.pendingUsers}</div>
                  <p className="text-xs text-gray-600 mt-1">Awaiting approval</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">Total Balance</CardTitle>
                  <DollarSign className="w-4 h-4 text-green-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">${stats.totalBalance.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</div>
                  <p className="text-xs text-gray-600 mt-1">Across all accounts</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">Total Transactions</CardTitle>
                  <Activity className="w-4 h-4 text-purple-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{stats.totalTransactions}</div>
                  <p className="text-xs text-gray-600 mt-1">All time</p>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle>Recent Transactions</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {stats?.recentTransactions?.map((txn) => (
                    <div key={txn.id} className="flex items-center justify-between border-b pb-3">
                      <div className="flex items-center space-x-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          txn.type === 'credit' || txn.type === 'transfer_in' ? 'bg-green-100' : 'bg-red-100'
                        }`}>
                          {txn.type === 'credit' || txn.type === 'transfer_in' ? (
                            <ArrowUpCircle className="w-5 h-5 text-green-600" />
                          ) : (
                            <ArrowDownCircle className="w-5 h-5 text-red-600" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{txn.description}</p>
                          <p className="text-sm text-gray-600">Acc: {txn.accountNumber}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={`font-bold ${
                          txn.type === 'credit' || txn.type === 'transfer_in' ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {txn.type === 'credit' || txn.type === 'transfer_in' ? '+' : '-'}
                          ${txn.amount.toLocaleString('en-US', {minimumFractionDigits: 2})}
                        </p>
                        <p className="text-xs text-gray-600">{new Date(txn.createdAt).toLocaleDateString()}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <Card>
            <CardHeader>
              <CardTitle>User Management</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Account</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Balance</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {users.map((user) => (
                      <tr key={user.id}>
                        <td className="px-4 py-3 text-sm font-medium">{user.accountNumber}</td>
                        <td className="px-4 py-3 text-sm">{user.firstName} {user.lastName}</td>
                        <td className="px-4 py-3 text-sm">{user.email}</td>
                        <td className="px-4 py-3 text-sm font-medium">${user.balance.toLocaleString('en-US', {minimumFractionDigits: 2})}</td>
                        <td className="px-4 py-3 text-sm">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            user.status === 'active' ? 'bg-green-100 text-green-800' :
                            user.status === 'pending' ? 'bg-orange-100 text-orange-800' :
                            user.status === 'suspended' ? 'bg-red-100 text-red-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {user.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <div className="flex space-x-2 flex-wrap gap-1">
                            {user.status === 'pending' && (
                              <Button size="sm" onClick={() => handleApprove(user.id)} className="bg-green-600 hover:bg-green-700">
                                <CheckCircle className="w-4 h-4" />
                              </Button>
                            )}
                            {user.status === 'active' && (
                              <>
                                <Button 
                                  size="sm" 
                                  variant="outline" 
                                  onClick={() => {
                                    setSelectedUser(user);
                                    setShowTransactionDialog(true);
                                  }}
                                  title="Credit/Debit"
                                >
                                  <CreditCard className="w-4 h-4" />
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="outline" 
                                  onClick={() => handleSuspend(user.id)}
                                  title="Suspend Account"
                                >
                                  <Ban className="w-4 h-4 text-orange-600" />
                                </Button>
                                {user.wireTransferSuspended ? (
                                  <Button 
                                    size="sm" 
                                    variant="outline" 
                                    onClick={() => handleUnsuspendWireTransfer(user.id, `${user.firstName} ${user.lastName}`)}
                                    title="Unsuspend Wire Transfer"
                                    className="bg-green-50"
                                  >
                                    <ArrowUpCircle className="w-4 h-4 text-green-600" />
                                  </Button>
                                ) : (
                                  <Button 
                                    size="sm" 
                                    variant="outline" 
                                    onClick={() => handleSuspendWireTransfer(user.id, `${user.firstName} ${user.lastName}`)}
                                    title="Suspend Wire Transfer"
                                    className="bg-yellow-50"
                                  >
                                    <ArrowUpCircle className="w-4 h-4 text-yellow-600" />
                                  </Button>
                                )}
                              </>
                            )}
                            {user.status === 'suspended' && (
                              <Button size="sm" onClick={() => handleActivateUser(user.id)} className="bg-green-600 hover:bg-green-700">
                                <CheckCircle2 className="w-4 h-4" />
                              </Button>
                            )}
                            <Button 
                              size="sm" 
                              variant="outline" 
                              onClick={() => handleResetPassword(user.id)}
                              title="Reset Password"
                            >
                              <Key className="w-4 h-4 text-purple-600" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline" 
                              onClick={() => handleTerminate(user.id)}
                              title="Terminate Account"
                            >
                              <UserX className="w-4 h-4 text-red-600" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline" 
                              onClick={() => handleDeleteUser(user.id, `${user.firstName} ${user.lastName}`)}
                              title="Delete User Permanently"
                              className="bg-red-50"
                            >
                              <Trash2 className="w-4 h-4 text-red-700" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Transactions Tab */}
        {activeTab === 'transactions' && (
          <Card>
            <CardHeader>
              <CardTitle>All Transactions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reference</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Account</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {transactions.map((txn) => (
                      <tr key={txn.id}>
                        <td className="px-4 py-3 text-sm font-mono">{txn.reference}</td>
                        <td className="px-4 py-3 text-sm">{txn.accountNumber}</td>
                        <td className="px-4 py-3 text-sm">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            txn.type === 'credit' || txn.type === 'transfer_in' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {txn.type}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm">${txn.amount.toLocaleString('en-US', {minimumFractionDigits: 2})}</td>
                        <td className="px-4 py-3 text-sm">{txn.description}</td>
                        <td className="px-4 py-3 text-sm">{new Date(txn.createdAt).toLocaleString()}</td>
                        <td className="px-4 py-3 text-sm">
                          <Button 
                            size="sm" 
                            variant="outline" 
                            onClick={() => handleDeleteTransaction(txn.id)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Messages Tab with Reply Functionality */}
        {activeTab === 'messages' && (
          <Card>
            <CardHeader>
              <CardTitle>Customer Support Messages</CardTitle>
              <p className="text-sm text-gray-600 mt-1">View and respond to customer messages</p>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {messages.filter(m => !m.isAdminReply).length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <MessageSquare className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                    <p>No messages yet</p>
                  </div>
                ) : (
                  (messages || []).filter(m => !m.isAdminReply).map((msg) => (
                    <div key={msg.id} className="border rounded-lg p-4 bg-white shadow-sm">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <p className="font-medium text-gray-900">{msg.userName} ({msg.userAccountNumber})</p>
                          <p className="text-sm text-gray-600">{new Date(msg.createdAt).toLocaleString()}</p>
                        </div>
                        <MessageSquare className="w-5 h-5 text-blue-600" />
                      </div>
                      <p className="text-gray-800 mb-4">{msg.message}</p>
                      
                      {/* Reply Button */}
                      <div className="flex justify-end">
                        <Button 
                          size="sm"
                          onClick={() => {
                            setSelectedChatUser(msg.userId);
                            setShowChatDialog(true);
                          }}
                          className="bg-blue-600 hover:bg-blue-700"
                        >
                          <MessageSquare className="w-4 h-4 mr-2" />
                          Reply to Customer
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div className="space-y-6">
            <Card>
            <CardHeader>
              <CardTitle>Admin Settings - Change Password</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleAdminPasswordChange} className="space-y-6 max-w-md">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Current Password
                  </label>
                  <Input
                    type="password"
                    value={adminPasswordData.currentPassword}
                    onChange={(e) => setAdminPasswordData({...adminPasswordData, currentPassword: e.target.value})}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    New Password
                  </label>
                  <Input
                    type="password"
                    value={adminPasswordData.newPassword}
                    onChange={(e) => setAdminPasswordData({...adminPasswordData, newPassword: e.target.value})}
                    required
                    minLength={6}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Confirm New Password
                  </label>
                  <Input
                    type="password"
                    value={adminPasswordData.confirmPassword}
                    onChange={(e) => setAdminPasswordData({...adminPasswordData, confirmPassword: e.target.value})}
                    required
                    minLength={6}
                  />
                </div>

                <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                  Change Password
                </Button>
              </form>
            </CardContent>
          </Card>
          </div>
        )}
      </div>

      {/* Transaction Dialog - Outside the table */}
      <Dialog open={showTransactionDialog} onOpenChange={setShowTransactionDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Manage Account: {selectedUser?.accountNumber}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="block text-sm font-medium mb-2">Amount</label>
              <Input
                type="number"
                step="0.01"
                value={transactionData.amount}
                onChange={(e) => setTransactionData({...transactionData, amount: e.target.value})}
                placeholder="Enter amount"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Description</label>
              <Textarea
                value={transactionData.description}
                onChange={(e) => setTransactionData({...transactionData, description: e.target.value})}
                placeholder="Enter description"
              />
            </div>
            <div className="flex space-x-2">
              <Button onClick={handleCredit} className="flex-1 bg-green-600 hover:bg-green-700">
                Credit Account
              </Button>
              <Button onClick={handleDebit} className="flex-1 bg-red-600 hover:bg-red-700">
                Debit Account
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Chat Reply Dialog */}
      <Dialog open={showChatDialog} onOpenChange={setShowChatDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reply to Customer</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleChatReply} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Your Reply
              </label>
              <Textarea
                value={chatReply}
                onChange={(e) => setChatReply(e.target.value)}
                placeholder="Type your response to the customer..."
                rows={5}
                required
              />
            </div>
            <div className="flex space-x-2">
              <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700">
                <MessageSquare className="w-4 h-4 mr-2" />
                Send Reply
              </Button>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setShowChatDialog(false)}
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
      </div>
      
      {/* Session Timeout Warning Modal */}
      <SessionTimeoutModal
        isOpen={showSessionWarning}
        onExtend={handleExtendSession}
        onLogout={handleLogout}
      />
    </div>
  );
};

export default AdminDashboard;
